import { useEffect, useState } from "react";
import { Star } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

/**
 * A pretty prompt callout with a star-to-favorite button.
 * Keeps its favorited state in local component state and syncs with the backend.
 *
 * Props:
 *  - weekNumber: 1..11
 *  - prompt: current rotated prompt text
 *  - eyebrow: small uppercase label shown above the prompt (e.g. "Today's prompt")
 *  - onToggled?: optional callback(nextStarred:boolean) fired after a successful toggle
 */
export default function PromptCallout({ weekNumber, prompt, eyebrow = "Today's prompt", onToggled }) {
  const [starred, setStarred] = useState(false);
  const [busy, setBusy] = useState(false);

  // Check whether the current prompt is already starred by this user
  useEffect(() => {
    let cancelled = false;
    if (!prompt || !weekNumber) return;
    (async () => {
      try {
        const { data } = await api.get("/prompts/favorites");
        if (cancelled) return;
        const match = data.find(
          (f) => f.week_number === +weekNumber && f.prompt.trim() === prompt.trim(),
        );
        setStarred(!!match);
      } catch {
        /* silent — favorites are additive */
      }
    })();
    return () => { cancelled = true; };
  }, [weekNumber, prompt]);

  const toggle = async () => {
    if (!prompt || busy) return;
    setBusy(true);
    try {
      if (starred) {
        await api.delete("/prompts/favorites", {
          data: { week_number: +weekNumber, prompt },
        });
        setStarred(false);
        onToggled?.(false);
        toast("Removed from your starred prompts.");
      } else {
        await api.post("/prompts/favorites", { week_number: +weekNumber, prompt });
        setStarred(true);
        onToggled?.(true);
        toast.success("Starred — we'll resurface it in your Sunday digest.");
      }
    } catch {
      toast.error("Could not update that star.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="prompt-callout"
      className="mb-5 border-l-2 border-accent/70 bg-accent/5 pl-5 pr-4 py-3 flex items-start gap-3">
      <div className="flex-1 min-w-0">
        <div className="text-[10px] uppercase tracking-[0.2em] text-accent/80 mb-1">
          {eyebrow}
        </div>
        <p className="font-serif italic text-lg leading-relaxed break-words">{prompt}</p>
      </div>
      <button
        type="button"
        onClick={toggle}
        disabled={busy}
        data-testid="prompt-star"
        aria-label={starred ? "Unstar prompt" : "Star this prompt"}
        title={starred ? "Starred — in your Sunday digest" : "Star this prompt"}
        className={`shrink-0 mt-1 p-1.5 rounded-full transition-colors ${
          starred ? "text-primary hover:text-primary/80" : "text-muted-foreground hover:text-primary"
        } ${busy ? "opacity-50" : ""}`}
      >
        <Star className="w-5 h-5" fill={starred ? "currentColor" : "none"} />
      </button>
    </div>
  );
}
